import { addDays, parseIsoDate } from "./date.ts";
import {
  DomainError,
  type EffectiveRuleVersion,
  type IsoDate,
  type Operation,
  type RepeatRule,
} from "./model.ts";
import { assertRepeatRule, assertRuleCapacity } from "./repeat.ts";
import { ruleVersionOn } from "./repeat.ts";

export interface SchedulePhaseInput {
  readonly id: string;
  readonly effectiveFrom: IsoDate;
  readonly rule: RepeatRule;
}

export type ScheduleRuleChange =
  | Readonly<{
      kind: "permanent";
      id: string;
      effectiveFrom: IsoDate;
      rule: RepeatRule;
    }>
  | Readonly<{
      kind: "temporary";
      id: string;
      resumeId: string;
      effectiveFrom: IsoDate;
      effectiveToExclusive: IsoDate;
      rule: RepeatRule;
    }>;

const GREGORIAN_CYCLE_MONTHS = 400 * 12;

function assertPhase(phase: SchedulePhaseInput): void {
  if (phase.id.trim().length === 0) {
    throw new DomainError("INVALID_RULE_VERSION_ID", "schedule phase id is required");
  }
  parseIsoDate(phase.effectiveFrom);
  assertRepeatRule(phase.rule);
}

function calendarMonthIndex(date: IsoDate): number {
  const parsed = new Date(parseIsoDate(date));
  return parsed.getUTCFullYear() * 12 + parsed.getUTCMonth();
}

function representativeGregorianMonth(monthIndex: number): IsoDate {
  const year = Math.floor(monthIndex / 12);
  const month = monthIndex - year * 12;
  const cycleYear = ((year % 400) + 400) % 400;
  return `${String(2000 + cycleYear).padStart(4, "0")}-${String(month + 1).padStart(2, "0")}-01`;
}

function gregorianMonthShape(monthStart: IsoDate): string {
  const parsed = new Date(parseIsoDate(monthStart));
  const days = new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth() + 1, 0)).getUTCDate();
  return `${days}|${parsed.getUTCDay()}`;
}

function capacityRuleKey(rule: RepeatRule): string {
  const weekdays = rule.weekdays === null ? "*" : [...rule.weekdays].sort((left, right) => left - right).join(",");
  const period = rule.period.kind === "rolling" ? `rolling:${rule.period.days}` : rule.period.kind;
  const part = rule.part.kind === "whole" ? "whole" : `${rule.part.kind}:${rule.part.days}`;
  return `${rule.quota}|${weekdays}|${period}|${part}`;
}

/** Calendar-month capacity varies with month length and weekday alignment. A
 * complete Gregorian pattern repeats every 400 years, so open phases need no
 * unbounded scan and identical rule/month patterns can share validation. */
function assertPhaseCapacity(
  rule: RepeatRule,
  effectiveFrom: IsoDate,
  effectiveToExclusive: IsoDate | null,
  checkedMonthlyPatterns: Set<string>,
): void {
  if (rule.period.kind !== "month") {
    assertRuleCapacity(rule, effectiveFrom);
    return;
  }

  const firstMonth = calendarMonthIndex(effectiveFrom);
  const intersectedMonths = effectiveToExclusive === null
    ? GREGORIAN_CYCLE_MONTHS
    : calendarMonthIndex(addDays(effectiveToExclusive, -1)) - firstMonth + 1;
  const monthsToCheck = Math.min(intersectedMonths, GREGORIAN_CYCLE_MONTHS);
  const ruleKey = capacityRuleKey(rule);
  for (let offset = 0; offset < monthsToCheck; offset += 1) {
    const monthIndex = firstMonth + offset;
    const representativeMonth = representativeGregorianMonth(monthIndex);
    // Capacity is identical when month length and its starting weekday match,
    // reducing a 4,800-month cycle to at most 28 actual capacity calculations.
    const cacheKey = `${ruleKey}|${gregorianMonthShape(representativeMonth)}`;
    if (checkedMonthlyPatterns.has(cacheKey)) continue;
    assertRuleCapacity(rule, representativeMonth);
    checkedMonthlyPatterns.add(cacheKey);
  }
}

export function operationStartDate(operation: Operation): IsoDate {
  const starts = [
    ...operation.activePeriods.map((period) => period.from),
    ...operation.ruleVersions.map((version) => version.effectiveFrom),
  ];
  for (const start of starts) parseIsoDate(start);
  starts.sort();
  const start = starts[0];
  if (!start) throw new DomainError("OPERATION_HAS_NO_ACTIVE_PERIOD", `Operation ${operation.id} has no active period`);
  return start;
}

/**
 * Canonical schedule phases tile the operation timeline. End dates are derived
 * from the next phase, so callers cannot submit gaps, overlaps, or two open
 * phases. Duplicate dates and ids are rejected rather than resolved by input
 * order.
 */
export function canonicalSchedulePhases(
  operationStart: IsoDate,
  phases: readonly SchedulePhaseInput[],
): EffectiveRuleVersion[] {
  parseIsoDate(operationStart);
  if (phases.length === 0) {
    throw new DomainError("EMPTY_SCHEDULE", "an operation requires at least one schedule phase");
  }
  const ids = new Set<string>();
  const dates = new Set<string>();
  for (const phase of phases) {
    assertPhase(phase);
    if (ids.has(phase.id)) throw new DomainError("DUPLICATE_RULE_VERSION", `schedule phase id ${phase.id} is duplicated`);
    if (dates.has(phase.effectiveFrom)) {
      throw new DomainError("RULE_VERSION_DATE_CONFLICT", `more than one schedule phase starts on ${phase.effectiveFrom}`);
    }
    ids.add(phase.id);
    dates.add(phase.effectiveFrom);
  }
  const sorted = [...phases].sort((left, right) => left.effectiveFrom.localeCompare(right.effectiveFrom));
  if (sorted[0]!.effectiveFrom !== operationStart) {
    throw new DomainError("SCHEDULE_START_MISMATCH", `the first schedule phase must start on ${operationStart}`);
  }
  const canonical = sorted.map((phase, index) => ({
    id: phase.id,
    effectiveFrom: phase.effectiveFrom,
    effectiveToExclusive: sorted[index + 1]?.effectiveFrom ?? null,
    rule: phase.rule,
  }));
  const checkedMonthlyPatterns = new Set<string>();
  for (const phase of canonical) {
    assertPhaseCapacity(
      phase.rule,
      phase.effectiveFrom,
      phase.effectiveToExclusive,
      checkedMonthlyPatterns,
    );
  }
  return canonical;
}

/** Replace the complete timeline, including deliberate historical repairs. */
export function replaceScheduleTimeline(
  operation: Operation,
  phases: readonly SchedulePhaseInput[],
): Operation {
  return {
    ...operation,
    ruleVersions: canonicalSchedulePhases(operationStartDate(operation), phases),
  };
}

/**
 * Overlay one confirmed rule change on the current canonical timeline.
 * Application order defines precedence: the latest confirmed change replaces
 * every boundary inside its span. A temporary change restores the rule that
 * the pre-edit timeline would have applied when its range ends.
 */
export function applyScheduleRuleChange(
  operation: Operation,
  change: ScheduleRuleChange,
): Operation {
  const operationStart = operationStartDate(operation);
  parseIsoDate(change.effectiveFrom);
  assertRepeatRule(change.rule);
  if (change.id.trim().length === 0) {
    throw new DomainError("INVALID_RULE_VERSION_ID", "schedule change id is required");
  }
  if (change.effectiveFrom < operationStart) {
    throw new DomainError("RULE_CHANGE_BEFORE_OPERATION", "schedule changes cannot begin before the operation");
  }

  const current = canonicalSchedulePhases(operationStart, operation.ruleVersions.map((version) => ({
    id: version.id,
    effectiveFrom: version.effectiveFrom,
    rule: version.rule,
  })));
  const existingStart = current.find((phase) => phase.effectiveFrom === change.effectiveFrom);
  const replacement: SchedulePhaseInput = {
    id: existingStart?.id ?? change.id,
    effectiveFrom: change.effectiveFrom,
    rule: change.rule,
  };

  if (change.kind === "permanent") {
    return replaceScheduleTimeline(operation, [
      ...current.filter((phase) => phase.effectiveFrom < change.effectiveFrom),
      replacement,
    ]);
  }

  parseIsoDate(change.effectiveToExclusive);
  if (change.effectiveToExclusive <= change.effectiveFrom) {
    throw new DomainError("INVALID_RULE_RANGE", "temporary rule end must follow its start");
  }
  if (change.resumeId.trim().length === 0) {
    throw new DomainError("INVALID_RULE_VERSION_ID", "temporary rule resume id is required");
  }

  const underlyingAtEnd = ruleVersionOn(current, change.effectiveToExclusive);
  const existingEnd = current.find((phase) => phase.effectiveFrom === change.effectiveToExclusive);
  const outside = current.filter((phase) =>
    phase.effectiveFrom < change.effectiveFrom ||
    phase.effectiveFrom >= change.effectiveToExclusive
  );
  const next: SchedulePhaseInput[] = [...outside, replacement];
  if (!existingEnd) {
    next.push({
      id: change.resumeId,
      effectiveFrom: change.effectiveToExclusive,
      rule: underlyingAtEnd.rule,
    });
  }
  return replaceScheduleTimeline(operation, next);
}

/**
 * Insert or replace one phase and then re-derive every end date. Replacing a
 * date preserves its existing identity, avoiding the former same-day duplicate
 * failure. A caller may supply an empty id only when replacing an existing date.
 */
export function upsertSchedulePhase(
  operation: Operation,
  phase: SchedulePhaseInput,
): Operation {
  parseIsoDate(phase.effectiveFrom);
  assertRepeatRule(phase.rule);
  const current = operation.ruleVersions.map((version): SchedulePhaseInput => ({
    id: version.id,
    effectiveFrom: version.effectiveFrom,
    rule: version.rule,
  }));
  const sameDate = current.find((version) => version.effectiveFrom === phase.effectiveFrom);
  const replacement: SchedulePhaseInput = {
    ...phase,
    id: sameDate?.id ?? phase.id,
  };
  const next = sameDate
    ? current.map((version) => version.effectiveFrom === phase.effectiveFrom ? replacement : version)
    : [...current, replacement];
  return replaceScheduleTimeline(operation, next);
}

/** Removing the first phase would leave the operation without a rule at start. */
export function removeSchedulePhase(operation: Operation, phaseId: string): Operation {
  const sorted = [...operation.ruleVersions].sort((left, right) => left.effectiveFrom.localeCompare(right.effectiveFrom));
  const selected = sorted.find((phase) => phase.id === phaseId);
  if (!selected) throw new DomainError("UNKNOWN_RULE_VERSION", `schedule phase ${phaseId} does not exist`);
  if (selected === sorted[0]) throw new DomainError("CANNOT_REMOVE_FIRST_RULE", "the first schedule phase cannot be removed");
  return replaceScheduleTimeline(operation, sorted
    .filter((phase) => phase.id !== phaseId)
    .map((phase) => ({ id: phase.id, effectiveFrom: phase.effectiveFrom, rule: phase.rule })));
}
